import api, { AVAILABLE_WIDGETS_TYPE, DASHBOARD_TYPE, WIDGET_DATA_TYPE } from '../Api';
import { IActionResponse } from '../../dal/BaseDAL';
import {
    IDashboard,
    IDashboardCreateRequest,
    IDashboardUpdateRequest,
    IReportingWidget,
    IWidgetDataRequest,
    IWidgetDataResponse,
} from '../../models/Dashboard';
import { ICollectionResponse } from '../../models/ICollectionResponse';

const DASHBOARD_LIST = 'LIST';

interface GetDashboardsArgs {
    resource: string;
    storefrontId?: string;
}

interface GetDashboardArgs {
    resource: string;
    id: string;
}

interface CreateDashboardArgs {
    resource: string;
    request: IDashboardCreateRequest;
}

interface UpdateDashboardArgs {
    resource: string;
    id: string;
    request: IDashboardUpdateRequest;
}

interface DeleteDashboardArgs {
    resource: string;
    id: string;
}

interface GetWidgetDataArgs extends IWidgetDataRequest {
    resource: string;
    widgetType: string;
}

interface GetAvailableWidgetsArgs {
    resource: string;
}

function buildWidgetDataQueryString(args: GetWidgetDataArgs): string {
    const params = new URLSearchParams();
    if (args.from) params.set('from', args.from);
    if (args.to) params.set('to', args.to);
    if (args.timeframe) params.set('timeframe', args.timeframe);
    if (args.timeframeValue !== undefined) params.set('timeframeValue', args.timeframeValue.toString());
    if (args.interval) params.set('interval', args.interval);
    if (args.metric) params.set('metric', args.metric);
    if (args.resourceFilter) params.set('resourceFilter', args.resourceFilter);
    if (args.filter) params.set('filter', args.filter);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

const dashboardEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        getDashboards: build.query<ICollectionResponse<IDashboard>, GetDashboardsArgs>({
            query: (args) => {
                let url = `reporting/${args.resource}/dashboards`;
                if (args.storefrontId) {
                    url += `?storefrontId=${args.storefrontId}`;
                }
                return url;
            },
            providesTags: [{ type: DASHBOARD_TYPE, id: DASHBOARD_LIST }],
        }),
        getDashboard: build.query<IDashboard, GetDashboardArgs>({
            query: (args) => `reporting/${args.resource}/dashboards/${args.id}`,
            providesTags: (_result, _error, args) => [{ type: DASHBOARD_TYPE, id: args.id }],
        }),
        createDashboard: build.mutation<IActionResponse, CreateDashboardArgs>({
            query: (args) => ({
                url: `reporting/${args.resource}/dashboards`,
                method: 'POST',
                body: args.request,
            }),
            invalidatesTags: [{ type: DASHBOARD_TYPE, id: DASHBOARD_LIST }],
        }),
        updateDashboard: build.mutation<IActionResponse, UpdateDashboardArgs>({
            query: (args) => ({
                url: `reporting/${args.resource}/dashboards/${args.id}`,
                method: 'PUT',
                body: args.request,
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: DASHBOARD_TYPE, id: DASHBOARD_LIST },
                { type: DASHBOARD_TYPE, id: args.id },
            ],
        }),
        deleteDashboard: build.mutation<IActionResponse, DeleteDashboardArgs>({
            query: (args) => ({
                url: `reporting/${args.resource}/dashboards/${args.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: DASHBOARD_TYPE, id: DASHBOARD_LIST }],
        }),
        getAvailableWidgets: build.query<ICollectionResponse<IReportingWidget>, GetAvailableWidgetsArgs>({
            query: (args) => `reporting/${args.resource}/widgets`,
            providesTags: (_result, _error, args) => [{ type: AVAILABLE_WIDGETS_TYPE, id: args.resource }],
        }),
        getWidgetData: build.query<IWidgetDataResponse, GetWidgetDataArgs>({
            query: (args) => `reporting/${args.resource}/widgets/${args.widgetType}${buildWidgetDataQueryString(args)}`,
            providesTags: (_result, _error, args) => [{
                type: WIDGET_DATA_TYPE,
                id: `${args.widgetType}-${args.timeframe}-${args.timeframeValue}-${args.interval}-${args.metric}-${args.resourceFilter}`,
            }],
        }),
    }),
});

export const {
    useGetDashboardsQuery,
    useGetDashboardQuery,
    useCreateDashboardMutation,
    useUpdateDashboardMutation,
    useDeleteDashboardMutation,
    useGetAvailableWidgetsQuery,
    useGetWidgetDataQuery,
} = dashboardEndpoints;

export default dashboardEndpoints;
